import { isPlannerV2Action } from '@renderer/overlay/Sidepanel/hooks/staged-actions/confidence'
import { v1Client } from './v1Client'

export type FeedbackRating = 'positive' | 'negative'

/**
 * What a rating attaches to. A message subject rates that message; the
 * conversation lets the server flush it if it hasn't reached Postgres yet.
 * A conversation-only subject rates the conversation's latest assistant turn,
 * for surfaces whose content *is* that turn's output (a brief) and so carry
 * no authoring message id. A planner v2 staged action is rated on its own.
 */
export type FeedbackSubject =
  | { messageId: string; conversationId?: string; stagedActionId?: undefined }
  | { messageId?: undefined; conversationId: string; stagedActionId?: undefined }
  | { messageId?: undefined; conversationId?: undefined; stagedActionId: string }

/** The authoring-turn fields a chat-derived read carries, as the server names them. */
export interface ChatDerived {
  id?: string
  metadata?: Record<string, string> | null
  conversationId?: string | null
  triggeringLlmMessageId?: string | null
  feedback?: { rating?: number } | null
}

export function ratingFromFeedback(feedback: { rating?: number } | null | undefined): FeedbackRating | null {
  if (feedback?.rating == null || feedback.rating === 0) return null
  return feedback.rating > 0 ? 'positive' : 'negative'
}

/**
 * Feedback props for a read. A planner v2 action is always rated as itself,
 * even after a chat turn edits it and becomes its authoring message, so its
 * rating never moves. Anything else is rated through its authoring turn, and
 * a tool-written row with no message pointer has nothing to rate.
 */
export function feedbackSubject(
  read: ChatDerived,
): { subject: FeedbackSubject; initialRating: FeedbackRating | null } | undefined {
  if (read.id && isPlannerV2Action({ metadata: read.metadata ?? {} })) {
    return { subject: { stagedActionId: read.id }, initialRating: ratingFromFeedback(read.feedback) }
  }
  if (!read.triggeringLlmMessageId) return undefined
  return {
    subject: {
      messageId: read.triggeringLlmMessageId,
      ...(read.conversationId ? { conversationId: read.conversationId } : {}),
    },
    initialRating: ratingFromFeedback(read.feedback),
  }
}

export function feedbackSubjectKey(subject: FeedbackSubject): string {
  if (subject.stagedActionId !== undefined) return subject.stagedActionId
  return subject.messageId ?? subject.conversationId
}

/** The rating on a conversation's latest assistant turn; message subjects carry theirs on their own read. */
export async function readFeedback(
  { conversationId }: { conversationId: string },
  token: string,
  signal?: AbortSignal,
): Promise<FeedbackRating | null> {
  // The checked-in OpenAPI snapshot predates this route.
  const conversationFeedbackClient = v1Client as unknown as {
    GET(
      path: '/conversations/{id}/feedback',
      options: { params: { path: { id: string } }; headers: { Authorization: string }; signal?: AbortSignal },
    ): Promise<{
      response?: { status?: number }
      error?: unknown
      data?: { data: { rating?: number } | null }
    }>
  }
  const response = await conversationFeedbackClient.GET('/conversations/{id}/feedback', {
    params: { path: { id: conversationId } },
    headers: { Authorization: `Bearer ${token}` },
    signal,
  })
  if (response.response?.status === 404) return null
  if (response.error) throw new Error('Failed to load feedback')
  return ratingFromFeedback(response.data?.data)
}

export async function submitFeedback(
  subject: FeedbackSubject,
  token: string,
  { rating, message }: { rating: FeedbackRating; message: string },
): Promise<unknown> {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  const body = { type: 'other' as const, message, rating: rating === 'positive' ? 1 : -1 }
  if (subject.messageId) {
    const response = await v1Client.POST('/messages/{id}/feedback', {
      params: { path: { id: subject.messageId } },
      headers,
      body: { ...body, ...(subject.conversationId ? { conversationId: subject.conversationId } : {}) },
    })
    if (response.error) throw new Error('Failed to submit feedback')
    return response.data
  }
  if (subject.stagedActionId) {
    // The checked-in OpenAPI snapshot predates this route.
    const ratingClient = v1Client as unknown as {
      POST(
        path: '/staged-actions/{id}/rating',
        options: { params: { path: { id: string } }; headers: typeof headers; body: typeof body },
      ): Promise<{ error?: unknown; data?: unknown }>
    }
    const response = await ratingClient.POST('/staged-actions/{id}/rating', {
      params: { path: { id: subject.stagedActionId } },
      headers,
      body,
    })
    if (response.error) throw new Error('Failed to submit feedback')
    return response.data
  }
  const conversationId = subject.conversationId
  if (!conversationId) throw new Error('Feedback subject has no conversation')
  // The checked-in OpenAPI snapshot predates this route.
  const conversationFeedbackClient = v1Client as unknown as {
    POST(
      path: '/conversations/{id}/feedback',
      options: { params: { path: { id: string } }; headers: typeof headers; body: typeof body },
    ): Promise<{ error?: unknown; data?: unknown }>
  }
  const response = await conversationFeedbackClient.POST('/conversations/{id}/feedback', {
    params: { path: { id: conversationId } },
    headers,
    body,
  })
  if (response.error) throw new Error('Failed to submit feedback')
  return response.data
}
